import { useCallback, useEffect, useMemo, useState } from 'react';
import { metrajGoster } from '../lib/olcu';
import { api } from '../lib/api';
import { useOturum } from '../lib/oturum';
import { useTelefon } from '../lib/cihaz';
import {
  BelgeIkon,
  IskeleIkon,
  KalkanIkon,
  OkSagIkon,
  RevizeIkon,
  SiralaIkon,
  SokumIkon,
} from '../lib/ikon';


/**
 * İŞLERİM — saha kullanicisinin TEK ekrani (devir dokumani Bolum 6.11).
 * Prototip referansi: `renderMobile()`.
 *
 * Sahadaki kisinin telefonda yapabilecegi TEK SEY isini yurutmektir.
 * Analiz, pano ve rapor masaustunde kalir. v9'da mobilden kaldirilanlar:
 * alt sekme menusu, "Hazirlik" sekmesi, serbest kamera sekmesi, bildirimler.
 */

export interface SahaIs {
  id: string;
  isEmriNo: string | null;
  tanim: string;
  unite: string;
  /** İKİ AYRI TOPLAM — karışık iş tek sayıya indirgenmez (02.08.2026). */
  metrajM3: number;
  metrajM2: number;
  /** m³ + m² × 4 — tek sayı gerektiğinde (pano metriği, aylık kümülatif). */
  esdegerM3: number;
  durum: string;
  isEmriFotoVar: boolean;
  /** İşin AÇILDIĞI an — sıralamanın ve karttaki tarihin kaynağı. */
  olusturmaZamani: string;
  /** Prototip `ctaFor` — kartta sıradaki eylem yazar. */
  sonrakiEylem: string;
}

/**
 * SIRALAMA — varsayılan ESKİDEN YENİYE.
 *
 * ⚠️ Varsayılan tesadüfi değil: K6 "öncelik alanı yoktur, işler FIFO sırayla
 *    işlenir" diyor. En eski iş en üstte durur; sıradaki işi aramak gerekmez.
 *    Ters sıralama bir GÖRÜNÜM tercihi — işin sırasını değiştirmez, yalnız
 *    "en son ne geldi" sorusunu cevaplar.
 */
type Yon = 'asc' | 'desc';

/**
 * Prototip `sevFor` — kart kenar rengi durum sinifina gore.
 * Sol serit + eslesen ikon kutusu: saha kullanicisi listeyi TARAYARAK hangi isin
 * hangi asamada oldugunu goruyor, satir satir okumuyor.
 */
function durumStili(durum: string): { serit: string; kutu: string } {
  if (durum === 'kontrol') return { serit: 'border-l-info', kutu: 'bg-info-bg text-info' };
  if (durum === 'sokum' || durum === 'revize')
    return { serit: 'border-l-stok', kutu: 'bg-stok-bg text-stok' };
  if (durum === 'kurulum' || durum === 'hazirlik')
    return { serit: 'border-l-navy', kutu: 'bg-navy-bg text-navy' };
  return { serit: 'border-l-warn', kutu: 'bg-warn-bg text-warn' };
}

/** Durum -> ikon. Metni okumadan once sekil taniniyor. */
function durumIkonu(durum: string) {
  if (durum === 'sokum') return <SokumIkon boyut={18} />;
  if (durum === 'revize') return <RevizeIkon boyut={18} />;
  if (durum === 'kontrol') return <KalkanIkon boyut={18} />;
  if (durum === 'giris' || durum === 'kesif') return <BelgeIkon boyut={18} />;
  return <IskeleIkon boyut={18} />;
}

export function Saha({ onIsSec }: { onIsSec: (id: string) => void }) {
  const { ben } = useOturum();
  const telefon = useTelefon();
  const [isler, setIsler] = useState<SahaIs[]>([]);
  const [tumu, setTumu] = useState(false);
  const [yon, setYon] = useState<Yon>('asc');
  const [hata, setHata] = useState<string | null>(null);

  // "Tümü" radyosu YALNIZ şef/formen'de — kendi yapacaklarıyla karışmasın (Faz 7).
  const radyoVar = ben?.rol === 'sef' || ben?.rol === 'formen' || ben?.sistemRol === 'admin';

  const yukle = useCallback(async () => {
    try {
      const d = await api.get<{ isler: SahaIs[] }>(`/islerim${tumu ? '?tumu=1' : ''}`);
      setIsler(d.isler);
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Yüklenemedi');
    }
  }, [tumu]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  /* Sunucu zaten FIFO gönderiyor; burada yalnız GÖRÜNÜMÜ çeviriyoruz —
     sıralama için ağa gitmek, çevrimdışında listeyi sıralanamaz yapardı (T1). */
  const sirali = useMemo(() => {
    const k = (j: SahaIs) => new Date(j.olusturmaZamani).getTime();
    return [...isler].sort((a, b) => (yon === 'asc' ? k(a) - k(b) : k(b) - k(a)));
  }, [isler, yon]);

  return (
    <div className="mx-auto max-w-2xl p-4">
      {/* Başlık, sayı ve tarih kabuğun üst barında — burada tekrarlanmaz.
          Telefonda sayı zaten başlığın yanında; masaüstünde orada yok. */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-wait">
          {telefon && !tumu ? (
            'Sıradaki eylem her kartın sağında yazar.'
          ) : (
            <>
              <span className="num font-semibold text-navy">{isler.length}</span>{' '}
              {tumu ? 'iş (tümü)' : 'aktif iş'}
            </>
          )}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setYon((v) => (v === 'asc' ? 'desc' : 'asc'))}
            title={
              yon === 'asc'
                ? 'Eskiden yeniye (FIFO) — tersine çevirmek için tıklayın'
                : 'Yeniden eskiye — FIFO sırasına dönmek için tıklayın'
            }
            aria-label={`Tarihe göre sıralama: ${yon === 'asc' ? 'eskiden yeniye' : 'yeniden eskiye'}`}
            className="inline-flex items-center gap-1.5 rounded-(--radius-mod) border border-line-2 px-2.5 py-1.5 text-xs font-medium text-wait hover:border-navy hover:text-navy"
          >
            <SiralaIkon yon={yon} boyut={16} />
            {yon === 'asc' ? 'Eski → yeni' : 'Yeni → eski'}
          </button>
          {radyoVar && (
            <button
              onClick={() => setTumu((v) => !v)}
              className={`rounded-(--radius-mod) border px-3 py-1.5 text-sm ${
                tumu ? 'border-navy bg-navy text-white' : 'border-line-2 text-wait'
              }`}
            >
              Tümü
            </button>
          )}
        </div>
      </div>

      {hata && (
        <p className="mb-3 rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>
      )}

      {isler.length === 0 ? (
        <p className="px-3 py-10 text-center text-sm leading-relaxed text-wait">
          {tumu ? (
            'Kayıt yok.'
          ) : (
            <>
              Sana atanmış aktif iş yok.
              <br />
              Şef keşif/ekip verince burada belirir.
            </>
          )}
        </p>
      ) : (
        <div className="space-y-2">
          {sirali.map((j) => (
            <Kart key={j.id} j={j} tumu={tumu} onSec={() => onIsSec(j.id)} />
          ))}
        </div>
      )}

      <p className="mt-6 rounded-(--radius-mod) border border-line bg-surface-2 p-3 text-xs leading-relaxed text-wait">
        {tumu
          ? 'Tümü: takip amaçlı — tüm işlerin anlık durumu (salt görünüm).'
          : 'Tik ve fotoğraflar çevrimdışında da çalışır — bağlantı gelince gönderilir.'}
      </p>
    </div>
  );
}


/** Saha is karti — ikon kutusu + sol durum seridi + siradaki eylem. */
function Kart({ j, tumu, onSec }: { j: SahaIs; tumu: boolean; onSec: () => void }) {
  const s = durumStili(j.durum);
  return (
    <button
      onClick={onSec}
      className={`flex w-full items-start gap-3 rounded-(--radius-kart) border border-l-[5px] border-line bg-surface p-3.5 text-left shadow-(--shadow-mod) active:bg-ground ${s.serit}`}
    >
      <span
        className={`flex h-9.5 w-9.5 shrink-0 items-center justify-center rounded-[11px] ${s.kutu}`}
      >
        {durumIkonu(j.durum)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="num text-xs text-muted">{j.isEmriNo ?? '—'}</span>
          {!j.isEmriFotoVar && (
            <span className="rozet border-warn-line bg-warn-bg text-[10px] text-warn">
              foto todo
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-sm leading-tight font-bold text-ink">{j.tanim}</span>
        <span className="num mt-0.5 block text-[11px] text-muted">
          {j.unite} ·{' '}
          {j.metrajM3 || j.metrajM2 ? metrajGoster(j) : 'keşif gerekli'}
          {/* Tarih GÖRÜNÜR olmalı: görünmeyen bir alana göre sıralamak,
              listenin neden değiştiğini anlaşılmaz kılar. */}
          {' · '}
          {new Date(j.olusturmaZamani).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          })}
        </span>
      </span>

      {/* Prototip `ctaFor`: kartta SIRADAKİ EYLEM yazar — kullanıcı işe girmeden bilir */}
      {!tumu && (
        <span className="flex shrink-0 items-center gap-0.5 pt-1 text-xs font-semibold text-navy">
          {j.sonrakiEylem}
          <OkSagIkon boyut={14} />
        </span>
      )}
    </button>
  );
}
